/**
 * @file TemplateList.tsx
 * @description Компактный список шаблонов документов (phase19.31, часть 3;
 * разбор A: строка 64): название · «по умолчанию» и «в архиве» метками ·
 * вид документа второй линией · «⋯» с «Изменить» и «Удалить».
 *
 * Кусков HTML-кода в списке нет — только то, по чему шаблон узнают. Вся
 * строка открывает окно редактирования; «Удалить» — в «⋯» последним,
 * подтверждение спрашивает страница.
 */

import { Pencil, Trash2 } from "lucide-react";

import { Badge } from "../../ui/badge";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { DOCUMENT_KIND_LABEL, type DocumentTemplateDto } from "../../../lib/api-crm";
import { cn } from "@/lib/utils";

interface Props {
  templates: DocumentTemplateDto[];
  onEdit: (template: DocumentTemplateDto) => void;
  onDelete: (template: DocumentTemplateDto) => void;
}

const ROW_CLASS =
  "flex min-h-16 min-w-0 flex-1 flex-col justify-center gap-0.5 px-4 py-2 text-left outline-none transition-colors hover:bg-[var(--row-hover)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

export function TemplateList({ templates, onEdit, onDelete }: Props) {
  return (
    <ul aria-label="Шаблоны документов" className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)]")}>
      {templates.map((t) => (
        <li key={t.id} className="flex items-stretch">
          <button type="button" className={ROW_CLASS} onClick={() => onEdit(t)} aria-label={`Изменить шаблон «${t.name}»`}>
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="type-body truncate font-medium text-on-surface">{t.name}</span>
              {t.isDefault ? <Badge variant="outline">по умолчанию</Badge> : null}
              {!t.isActive ? <Badge variant="neutral">в архиве</Badge> : null}
            </span>
            <span className="type-caption truncate text-on-surface-variant">{DOCUMENT_KIND_LABEL[t.kind]}</span>
          </button>
          <div className="flex shrink-0 items-center pr-2">
            <RowActionsMenu
              size="compact"
              title={`Шаблон «${t.name}»`}
              ariaLabel={`Действия с шаблоном «${t.name}»`}
              actions={[
                { key: "edit", label: "Изменить", icon: Pencil, run: () => onEdit(t) },
                { key: "delete", label: "Удалить", icon: Trash2, danger: true, run: () => onDelete(t) },
              ]}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
